import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import TaskNumber from 'contents/ProtocolEditor/Components/TaskNumber';

import {useClickOutside} from 'lib/customHooks';
import {checkAndSetSelectedTipChannelByTask} from 'stores/business/taskListBusinessAction';
import '../../Task.scss';

import Spinner from 'components/Spinner/Spinner';
import TaskExecutedTick from 'components/TaskExecutedTick';
import TaskWrapper from 'contents/ProtocolEditor/TaskList/Task/Components/TaskWrapper';
import TaskTitle from 'contents/ProtocolEditor/TaskList/Task/Components/TaskTitle';
import TaskAction from 'contents/ProtocolEditor/TaskList/Task/Components/TaskAction';
import {CardType, ExecutionStatus, TaskType} from 'constants/dataEnum';
import {useDispatch, useSelector} from 'react-redux';
import {setSimpleTransferEditedType} from 'stores/actions/protocolEditorAction';
import {getSimpleTransferEditedType} from 'stores/selectors/protocolEditorSelectors';
import {setTaskIsSelected} from 'stores/actions/cardStateAction';
import {resetTipChannel} from 'stores/business/virtualTrayBusiness';
import {selectOpenedProtocolIsEjectToCaddy} from 'stores/selectors/protocolSelectors';
import TaskPickTipDescription from './TaskDescription/TaskPickTipDescription';

function TaskPickTip({
  // #region common props
  task,
  runtimeMode,
  readOnly,
  executionStatus,
  onDelete,
  onCheckedChange,
  // #endregion common props
  // #region dragging business
  onDrag,
  isDragging,
  disableDrag,
  // #endRegion dragging business
  excludedComponentRef,
  ejectTipTasksExists,
  onSelectTask,
}) {
  const dispatch = useDispatch();
  const pickTipTaskRef = useRef(null);
  const simpleTransferEditedType = useSelector(getSimpleTransferEditedType);
  const isEjectToCaddy = useSelector(selectOpenedProtocolIsEjectToCaddy);

  const {ID, number, rowTipCount, columnTipCount, isSelected, isIgnored} = task || {};

  const onEdit = () => {
    onSelectTask(ID, CardType.SIMPLE_TRANSFER);
    dispatch(setSimpleTransferEditedType(TaskType.PICK_TIP));
    dispatch(checkAndSetSelectedTipChannelByTask(task));
  };

  const onCloseEditablePickTip = () => {
    if (simpleTransferEditedType === TaskType.PICK_TIP) {
      dispatch(setSimpleTransferEditedType(null));
      dispatch(setTaskIsSelected(ID, false));

      if (ejectTipTasksExists) {
        dispatch(resetTipChannel());
      }
    }
  };

  useClickOutside(pickTipTaskRef, onCloseEditablePickTip, excludedComponentRef);

  return (
    <TaskWrapper
      ref={pickTipTaskRef}
      disableDrag={disableDrag}
      onDrag={onDrag}
      isDragging={isDragging}
      isSelected={isSelected}
    >
      <TaskNumber number={number} isHighlighted={isSelected} />
      <TaskTitle
        title="Pick tip(s)"
        description={
          <TaskPickTipDescription
            rowTipCount={rowTipCount}
            columnTipCount={columnTipCount}
            showCaddyNumber={isEjectToCaddy}
            caddyNumber={task.labwareID}
          />
        }
      />
      {!runtimeMode && !readOnly && (
        <TaskAction
          onEdit={onEdit}
          onDelete={onDelete}
          isIgnored={isIgnored}
          onIgnoreCheckboxChanged={(val) => onCheckedChange(ID, val)}
        />
      )}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTING && <Spinner />}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTED && <TaskExecutedTick />}
    </TaskWrapper>
  );
}

TaskPickTip.propTypes = {
  runtimeMode: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  executionStatus: PropTypes.string.isRequired,
  onDrag: PropTypes.func.isRequired,
  disableDrag: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  ejectTipTasksExists: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  task: PropTypes.object.isRequired,
  excludedComponentRef: PropTypes.any.isRequired,
  onSelectTask: PropTypes.func.isRequired,
};

export default TaskPickTip;
